<script lang="ts" module>
    export type Phase = 'idle' | 'brewing' | 'glow' | 'burst' | 'boom';
</script>

<script lang="ts">
    /**
     * 연금술 솥 — 연출(작업지시서 6절 · 2단계).
     *
     * - 물약은 넣은 재료 색으로 섞여 바뀐다(color). 거품과 김이 늘 올라오고, 불을 때면 더 세게 끓는다.
     * - 결과가 나오기 직전 등급 빛이 먼저 새어 나온다(glow) — 서버 답은 이미 와 있고, 보여 주는 순서만 늦춘다.
     * - 성공하면 빛기둥과 파티클(burst). 등급이 높을수록 크다. 실패하면 검은 연기(boom).
     * 화면 낭독기에는 읽히지 않는다 — 결과는 결과 팝업이 말한다.
     */
    import { GRADE_COLORS } from './lib/look';

    let { color, phase = 'idle', grade = 0, beat = 0 }: { color: string; phase?: Phase; grade?: number; beat?: number } = $props();

    // 일반 등급의 색(연회색)은 빛으로는 거의 안 보여서, 일반이면 물약 색으로 빛난다
    const glowColor = $derived(grade > 0 ? (GRADE_COLORS[grade] ?? GRADE_COLORS[0]) : color);
    // 등급이 높을수록 파티클이 많다 — 일반 10 · 전설 34
    const particles = $derived(
        Array.from({ length: 10 + grade * 6 }, (_, i) => {
            const angle = (Math.PI * 2 * i) / (10 + grade * 6) + Math.random() * 0.4;
            const distance = 40 + Math.random() * (40 + grade * 18);
            return { dx: Math.cos(angle) * distance, dy: Math.sin(angle) * distance - 30, delay: Math.random() * 0.12, size: 4 + Math.random() * (3 + grade) };
        }),
    );
</script>

<div class="cauldron phase-{phase}" style="--potion: {color}; --glow: {glowColor}" aria-hidden="true">
    {#if phase === 'glow' || phase === 'burst'}
        <div class="aura"></div>
    {/if}
    {#if phase === 'burst'}
        {#key beat}
            <div class="pillar" class:big={grade >= 3}></div>
            <div class="sparks">
                {#each particles as p, i (i)}
                    <span style="--dx: {p.dx}px; --dy: {p.dy}px; --delay: {p.delay}s; --size: {p.size}px"></span>
                {/each}
            </div>
        {/key}
    {/if}
    {#if phase === 'boom'}
        {#key beat}
            <div class="smoke">
                <span></span><span></span><span></span><span></span>
            </div>
        {/key}
    {/if}

    <div class="steam"><span></span><span></span></div>
    <div class="pot-body">
        <div class="rim"></div>
        <div class="liquid">
            <span class="b b1"></span><span class="b b2"></span><span class="b b3"></span><span class="b b4"></span>
        </div>
    </div>
    <div class="fire"><span></span><span></span><span></span></div>
</div>

<style>
    .cauldron {
        --potion: #ec4899;
        --glow: #d1d5db;
        position: absolute;
        left: 50%;
        bottom: 0.1rem;
        width: 5.6rem;
        height: 3.6rem;
        transform: translateX(-50%);
        pointer-events: none;
    }

    /* 솥 몸통 — 무쇠 */
    .pot-body {
        position: absolute;
        left: 0.35rem;
        right: 0.35rem;
        bottom: 0.55rem;
        height: 2.4rem;
        border-radius: 0.6rem 0.6rem 2.2rem 2.2rem;
        background: radial-gradient(circle at 30% 30%, #4b5563, #1f2937 70%);
        box-shadow: inset 0 -6px 10px rgba(0, 0, 0, 0.45);
        overflow: visible;
    }

    .rim {
        position: absolute;
        left: -0.3rem;
        right: -0.3rem;
        top: -0.35rem;
        height: 0.75rem;
        border-radius: 50%;
        background: #374151;
        box-shadow: inset 0 2px 0 rgba(255, 255, 255, 0.15);
    }

    /* 물약 — 넣은 재료 색이 섞인다 */
    .liquid {
        position: absolute;
        left: 0.05rem;
        right: 0.05rem;
        top: -0.2rem;
        height: 0.55rem;
        border-radius: 50%;
        background: radial-gradient(ellipse at 40% 40%, color-mix(in srgb, var(--potion) 70%, white), var(--potion));
        box-shadow: 0 0 10px color-mix(in srgb, var(--potion) 60%, transparent);
        transition:
            background 0.4s ease,
            box-shadow 0.4s ease;
        overflow: visible;
    }

    .phase-brewing .liquid {
        animation: swirl 0.5s ease-in-out infinite alternate;
    }

    .phase-boom .liquid {
        background: #111827;
        box-shadow: none;
    }

    /* 거품 */
    .b {
        position: absolute;
        bottom: 0.15rem;
        width: 0.35rem;
        height: 0.35rem;
        border-radius: 50%;
        background: color-mix(in srgb, var(--potion) 40%, white);
        opacity: 0;
        animation: rise 2.4s ease-in infinite;
    }

    .b1 {
        left: 20%;
    }
    .b2 {
        left: 45%;
        animation-delay: 0.7s;
    }
    .b3 {
        left: 68%;
        animation-delay: 1.3s;
    }
    .b4 {
        left: 35%;
        animation-delay: 1.9s;
    }

    .phase-brewing .b {
        animation-duration: 0.7s;
    }

    /* 김 */
    .steam span {
        position: absolute;
        bottom: 2.6rem;
        width: 0.7rem;
        height: 1.4rem;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.55);
        filter: blur(3px);
        opacity: 0;
        animation: steam 3s ease-out infinite;
    }

    .steam span:first-child {
        left: 35%;
    }

    .steam span:last-child {
        left: 55%;
        animation-delay: 1.5s;
    }

    .phase-brewing .steam span {
        animation-duration: 1.1s;
    }

    /* 불 */
    .fire {
        position: absolute;
        left: 50%;
        bottom: 0;
        width: 3rem;
        height: 0.7rem;
        transform: translateX(-50%);
        display: flex;
        justify-content: space-around;
        align-items: flex-end;
    }

    .fire span {
        width: 0.55rem;
        height: 0.6rem;
        border-radius: 50% 50% 40% 40%;
        background: radial-gradient(circle at 50% 70%, #fde68a, #f97316 60%, #dc2626);
        animation: flicker 0.4s ease-in-out infinite alternate;
        transform-origin: 50% 100%;
    }

    .fire span:nth-child(2) {
        animation-delay: 0.15s;
    }

    .phase-brewing .fire span {
        height: 0.9rem;
        animation-duration: 0.15s;
    }

    .phase-boom .fire span {
        opacity: 0;
    }

    /* 등급 빛이 먼저 새어 나온다 */
    .aura {
        position: absolute;
        left: 50%;
        bottom: 1.4rem;
        width: 9rem;
        height: 9rem;
        transform: translate(-50%, 50%);
        border-radius: 50%;
        background: radial-gradient(circle, color-mix(in srgb, var(--glow) 75%, white) 0%, color-mix(in srgb, var(--glow) 45%, transparent) 35%, transparent 70%);
        animation: aura 0.9s ease-in-out infinite alternate;
    }

    .pillar {
        position: absolute;
        left: 50%;
        bottom: 2rem;
        width: 2.2rem;
        height: 12rem;
        transform: translateX(-50%);
        transform-origin: 50% 100%;
        background: linear-gradient(to top, color-mix(in srgb, var(--glow) 85%, white), color-mix(in srgb, var(--glow) 30%, transparent) 70%, transparent);
        filter: blur(2px);
        animation: pillar 0.9s ease-out forwards;
    }

    .pillar.big {
        width: 3.4rem;
        height: 16rem;
    }

    .sparks span {
        position: absolute;
        left: 50%;
        bottom: 2.2rem;
        width: var(--size);
        height: var(--size);
        border-radius: 50%;
        background: color-mix(in srgb, var(--glow) 70%, white);
        box-shadow: 0 0 8px var(--glow);
        opacity: 0;
        animation: spark 0.9s ease-out var(--delay) forwards;
    }

    .smoke span {
        position: absolute;
        left: 50%;
        bottom: 2rem;
        width: 2.4rem;
        height: 2.4rem;
        border-radius: 50%;
        background: radial-gradient(circle, rgba(17, 24, 39, 0.85), rgba(17, 24, 39, 0) 70%);
        animation: smoke 1.2s ease-out forwards;
    }

    .smoke span:nth-child(2) {
        --sx: -2.2rem;
        animation-delay: 0.08s;
    }
    .smoke span:nth-child(3) {
        --sx: 2.2rem;
        animation-delay: 0.16s;
    }
    .smoke span:nth-child(4) {
        --sx: 0.5rem;
        animation-delay: 0.24s;
    }

    @keyframes swirl {
        from {
            transform: scaleX(1);
        }
        to {
            transform: scaleX(0.94) translateY(-1px);
        }
    }

    @keyframes rise {
        0% {
            opacity: 0;
            transform: translateY(0) scale(0.6);
        }
        20% {
            opacity: 0.9;
        }
        100% {
            opacity: 0;
            transform: translateY(-1.6rem) scale(1.1);
        }
    }

    @keyframes steam {
        0% {
            opacity: 0;
            transform: translateY(0) scaleX(0.8);
        }
        30% {
            opacity: 0.6;
        }
        100% {
            opacity: 0;
            transform: translateY(-1.8rem) scaleX(1.4);
        }
    }

    @keyframes flicker {
        from {
            transform: scaleY(0.85);
        }
        to {
            transform: scaleY(1.15) skewX(-4deg);
        }
    }

    @keyframes aura {
        from {
            opacity: 0.55;
            transform: translate(-50%, 50%) scale(0.85);
        }
        to {
            opacity: 1;
            transform: translate(-50%, 50%) scale(1.08);
        }
    }

    @keyframes pillar {
        0% {
            opacity: 0;
            transform: translateX(-50%) scaleY(0.1);
        }
        25% {
            opacity: 1;
            transform: translateX(-50%) scaleY(1);
        }
        100% {
            opacity: 0;
            transform: translateX(-50%) scaleY(1.05);
        }
    }

    @keyframes spark {
        0% {
            opacity: 1;
            transform: translate(-50%, 0) scale(1);
        }
        100% {
            opacity: 0;
            transform: translate(calc(-50% + var(--dx)), var(--dy)) scale(0.3);
        }
    }

    @keyframes smoke {
        0% {
            opacity: 0.9;
            transform: translate(-50%, 0) scale(0.5);
        }
        100% {
            opacity: 0;
            transform: translate(calc(-50% + var(--sx, 0rem)), -4rem) scale(2.4);
        }
    }

    /* 움직임을 줄여 달라는 기기 설정 — 색만 바뀌고 움직이지 않는다 */
    @media (prefers-reduced-motion: reduce) {
        .cauldron *,
        .cauldron *::before {
            animation: none !important;
        }
        .sparks,
        .smoke,
        .pillar {
            display: none;
        }
    }
</style>
